import { Dates, type ExcelCell, getProxyEndDate, getProxyStartDate, getProxyTypeNoun } from 'venn-utils';
import { type UsedProxyWithParents } from 'venn-state';
import { compact } from 'lodash';

/**
 * Standard styles and headers for the Proxy Excel export
 */
const BOLD_STYLE = { bold: true };
const PROXY_HEADERS: ExcelCell[] = [
  { value: 'Investment Name', style: BOLD_STYLE },
  { value: 'Proxy Type', style: BOLD_STYLE },
  { value: 'Proxy Name', style: BOLD_STYLE },
  { value: 'Proxy Start', style: BOLD_STYLE },
  { value: 'Proxy End', style: BOLD_STYLE },
  { value: 'Subjects', style: BOLD_STYLE },
];

/**
 * Helper to create a consistent Excel row structure
 */
const createProxyRow = (
  fundName: string,
  proxyTypeNoun: string,
  proxyName: string | undefined,
  startDate: string,
  endDate: string,
  subjectsCell: ExcelCell,
): ExcelCell[] => [
  { value: fundName },
  { value: proxyTypeNoun },
  { value: proxyName },
  { value: startDate },
  { value: endDate },
  subjectsCell,
];

export const convertProxiesToExcel = (
  proxies: { [p: string]: UsedProxyWithParents[] },
  holdingProxyFF: boolean,
  hasLayeredProxyFF: boolean,
): ExcelCell[][] => {
  const dataRows = Object.values(proxies).flatMap((proxyGroup): ExcelCell[][] => {
    const proxy = proxyGroup[0];
    if (!proxy) {
      return [];
    }

    const { proxiedFrequency, proxyInfos, proxyMetaList, proxyNameList, fundName } = proxy;
    const hasLayeredView = hasLayeredProxyFF && proxyInfos.length > 0;

    const subjectsCell: ExcelCell = {
      value: proxy.parentSubjectNames.length ? proxy.parentSubjectNames.join(', ') : undefined,
    };

    const rows: ExcelCell[][] = [];

    // 1. PRIMARY PROXIES (Layered or Legacy Return)
    if (hasLayeredView) {
      proxyInfos
        .filter(({ proxyType }) => proxyType !== 'EXTRAPOLATE')
        .forEach(({ proxyName, proxyStartRange, proxyEndRange, proxyType }) => {
          const endDate = proxyType === 'BACKFILL' ? getProxyEndDate({ ...proxy, proxyType }) : proxyEndRange;
          rows.push(
            createProxyRow(
              fundName,
              getProxyTypeNoun(proxyType),
              proxyName,
              Dates.toDDMMMYYYY(proxyStartRange, proxiedFrequency),
              Dates.toDDMMMYYYY(endDate, proxiedFrequency),
              subjectsCell,
            ),
          );
        });
    } else if (proxy.proxyVariant === 'RETURN' && proxy.proxyType !== 'EXTRAPOLATE') {
      rows.push(
        createProxyRow(
          fundName,
          getProxyTypeNoun(proxy.proxyType),
          proxy.proxyName,
          Dates.toDDMMMYYYY(getProxyStartDate(proxy), proxiedFrequency),
          Dates.toDDMMMYYYY(getProxyEndDate(proxy), proxiedFrequency),
          subjectsCell,
        ),
      );
    }

    // 2. EXTRAPOLATION PROXIES
    const extrapolationProxyInfo = proxyInfos.find(
      ({ extrapolate, proxyType }) => extrapolate || proxyType === 'EXTRAPOLATE',
    );

    if (extrapolationProxyInfo || proxy.extrapolate || proxy.proxyType === 'EXTRAPOLATE') {
      rows.push(
        createProxyRow(
          fundName,
          getProxyTypeNoun('EXTRAPOLATE'),
          extrapolationProxyInfo?.proxyName ?? proxy.proxyName,
          Dates.toDDMMMYYYY(proxy.extrapolateStartDate, proxiedFrequency),
          Dates.toDDMMMYYYY(proxy.extrapolateEndDate, proxiedFrequency),
          subjectsCell,
        ),
      );
    }

    // 3. HOLDINGS PROXIES
    if (holdingProxyFF) {
      const holdingsIndex = proxyMetaList.findIndex(({ proxyVariant }) => proxyVariant === 'HOLDING');
      const holdingsName =
        holdingsIndex !== -1
          ? proxyNameList[holdingsIndex]
          : proxy.proxyVariant === 'HOLDING'
            ? proxy.proxyName
            : undefined;

      if (holdingsName) {
        rows.push(createProxyRow(fundName, 'Holdings', holdingsName, '-', '-', subjectsCell));
      }
    }

    return rows;
  });

  return compact([[{ value: 'Name', style: BOLD_STYLE }, { value: 'Proxies' }], [], PROXY_HEADERS, ...dataRows]);
};
